import { DestroyRef, Injectable, NgZone, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { distinctUntilChanged } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { CreateDeviceTool } from './tools/create-device.tool';
import { CreateUserTool } from './tools/create-user.tool';
import { ModelContext, WebMcpRegistration, WebMcpTool, getModelContext } from './webmcp.types';
import { errorResult, extractErrorMessage } from './webmcp-tool-helpers';

/**
 * Exposes the app's WebMCP tools to in-browser AI agents.
 *
 * Tools are only registered while someone is signed in, and each tool decides from the
 * user's role whether it is offered at all, so an agent never sees an action the person
 * could not perform in the UI. Tools run inside the page, so they reuse the session
 * cookie, XSRF header and interceptors of every other API call.
 */
@Injectable({
  providedIn: 'root',
})
export class WebMcpToolsService {
  private authService = inject(AuthService);
  private createUserTool = inject(CreateUserTool);
  private createDeviceTool = inject(CreateDeviceTool);
  private zone = inject(NgZone);
  private destroyRef = inject(DestroyRef);

  private modelContext: ModelContext | null = null;
  private registrations = new Map<string, { controller: AbortController; handle?: WebMcpRegistration }>();
  private initialized = false;

  init(): void {
    if (this.initialized) {
      return;
    }
    this.modelContext = getModelContext();
    if (!this.modelContext) {
      return; // Browser has no WebMCP support; nothing to expose.
    }
    this.initialized = true;

    this.authService.currentUser$
      .pipe(
        distinctUntilChanged((a: any, b: any) => a?.userCode === b?.userCode && Number(a?.roleId) === Number(b?.roleId)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((user) => this.syncTools(user));

    this.destroyRef.onDestroy(() => this.unregisterAll());
  }

  get registeredToolNames(): string[] {
    return [...this.registrations.keys()];
  }

  private syncTools(user: any): void {
    this.unregisterAll();
    if (!user) {
      return;
    }

    const tools = [this.createUserTool.build(user), this.createDeviceTool.build(user)].filter(
      (tool): tool is WebMcpTool => tool !== null,
    );
    tools.forEach((tool) => this.register(tool));
  }

  private register(tool: WebMcpTool): void {
    const controller = new AbortController();
    const warn = (error: unknown) => console.warn(`WebMCP: could not register tool "${tool.name}"`, error);

    try {
      const result = this.modelContext!.registerTool(
        {
          ...tool,
          // The browser calls tools outside Angular's zone; run them inside it so dialogs,
          // toasts and HTTP calls trigger change detection like a normal click would.
          execute: (input, client) =>
            this.zone.run(() => tool.execute(input ?? {}, client)).catch((error) => errorResult(extractErrorMessage(error))),
        },
        { signal: controller.signal },
      );

      if (result instanceof Promise) {
        // Current spec: registration is async and rejects e.g. on a duplicate name.
        this.registrations.set(tool.name, { controller });
        result.catch((error) => {
          if (this.registrations.get(tool.name)?.controller === controller) {
            this.registrations.delete(tool.name);
          }
          warn(error);
        });
      } else {
        this.registrations.set(tool.name, { controller, handle: result ?? undefined });
      }
    } catch (error) {
      warn(error);
    }
  }

  private unregisterAll(): void {
    this.registrations.forEach(({ controller, handle }, name) => {
      try {
        // Current spec unregisters on abort; the fallbacks cover earlier previews and polyfills.
        controller.abort();
        if (handle && typeof handle.unregister === 'function') {
          handle.unregister();
        } else {
          this.modelContext?.unregisterTool?.(name);
        }
      } catch (error) {
        console.warn(`WebMCP: could not unregister tool "${name}"`, error);
      }
    });
    this.registrations.clear();
  }
}
